/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

/**
 * Core web kökü ve göreli varlık URL çözümleme (dış “Hesabım” linki gibi UI navigasyonları).
 *
 * Kamu kök URL ayrı env anahtarı değildir: kök `.env` `BASE_URL` + `APPLICATION_CODE`
 * (Vite define: `VITE_BASE_URL`, `VITE_APPLICATION_CODE`). Uydu/staging istisnası için
 * isteğe bağlı `VITE_CORE_WEB_PUBLIC_BASE_URL`.
 *
 * Kurumsal BFF kuralı: tarayıcı protected/Core-backed API'lere doğrudan `/core/api/...`
 * gitmez. Frontend kendi ürün API'sini (`/api/...`) çağırır; gerekiyorsa ürün backend'i
 * Core'a server-side proxy yapar.
 */

import { withViteBasePathForApiDms } from './api-same-origin.utils';

function stripTrailingSlashes(s: string): string {
  return s.replace(/\/+$/, '').trim();
}

/**
 * Core SPA’nın dışarıdan çağrılabilir kök URL’i (örn. https://host/core).
 * Öncelik: isteğe bağlı override; aksi halde `VITE_BASE_URL` + ürün segmenti.
 */
export function getCoreApiBaseUrl(): string {
  const explicit = stripTrailingSlashes(import.meta.env.VITE_CORE_WEB_PUBLIC_BASE_URL || '');
  if (explicit) return explicit;

  const platform = stripTrailingSlashes(
    import.meta.env.VITE_BASE_URL || import.meta.env.VITE_PLATFORM_URL || ''
  );
  const rawCode = (import.meta.env.VITE_APPLICATION_CODE || '').trim();
  const codeLower = rawCode.toLowerCase();

  if (!platform || !codeLower) return '';

  // Core ürün derlemesi: kamu kökü BASE_URL + segment olarak küçük harf APPLICATION_CODE (örn. .../core)
  if (codeLower === 'core') {
    return `${platform}/${codeLower}`;
  }

  // Uydu: aynı BASE_URL üzerinde CORE segmenti varsayılan /core
  return `${platform}/core`;
}

/**
 * Portal uygulama listesi — tek kaynak Core `GET /api/apps` (`apps.api` → `apps.registry.ts`).
 *
 * Frontend her zaman ürün-local `/api/apps` der. Core üründe bu endpoint listeyi üretir;
 * uydularda backend aynı endpoint'i Core'a server-side proxy eder.
 */
export function getCentralAppsRegistryUrl(): string {
  return withViteBasePathForApiDms('/api/apps');
}

export function resolveCoreApiPath(relativeApiPath: string): string {
  const path = relativeApiPath.startsWith('/') ? relativeApiPath : `/${relativeApiPath}`;
  return withViteBasePathForApiDms(path);
}

function getCoreAssetBaseFromEnv(): string {
  return getCoreApiBaseUrl();
}

/** Core’dan gelen göreli URL’leri tarayıcıda yüklenebilir URL’e çevirir */
export function resolveCoreAssetUrl(url: string | null | undefined): string {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:')) return trimmed;
  if (trimmed.startsWith('//')) {
    return `${typeof window !== 'undefined' ? window.location.protocol : 'https:'}${trimmed}`;
  }
  const core = getCoreAssetBaseFromEnv();
  if (trimmed.startsWith('/')) {
    if (trimmed.startsWith('/api/') || trimmed.startsWith('/dms/')) {
      return withViteBasePathForApiDms(trimmed);
    }
    if (core) return `${core}${trimmed}`;
    return trimmed;
  }
  try {
    if (core) return new URL(trimmed, `${core}/`).toString();
    if (typeof window !== 'undefined') return new URL(trimmed, `${window.location.origin}/`).toString();
  } catch {
    return '';
  }
  return '';
}

/** Varsayılan “Hesabınızı Yönetin” URL: önce VITE_CORE_WEB_USER_SETTINGS_URL, sonra kamu Core kökü, sonra mevcut origin */
export function getDefaultUserSettingsUrl(): string {
  const explicit = (import.meta.env.VITE_CORE_WEB_USER_SETTINGS_URL || '').trim();
  if (explicit) return explicit.replace(/\/+$/, '');
  const coreBase = getCoreApiBaseUrl();
  if (coreBase) return `${coreBase}/settings/user`;
  if (typeof window !== 'undefined') {
    const base = (import.meta.env.VITE_BASE_PATH || '').replace(/\/+$/, '');
    return `${window.location.origin}${base}/settings/user`;
  }
  return '/settings/user';
}

/** Account management entry only; a profile link never requests a product grant. */
export function getDefaultUserExtensionsUrl(): string | null {
  const core = getCoreApiBaseUrl();
  return core ? `${core}/settings/extensions` : null;
}

/** Self-service entry on the Core host; selecting a company here does not
 * change the satellite shell's company state. No credential is put in the URL. */
export type UserExtensionFeature = 'READ' | 'SEND' | 'SHARED_READ' | 'SHARED_SEND' | 'CALENDAR_READ' | 'CALENDAR_WRITE' | 'SHARED_CALENDAR_WRITE';
const userExtensionFeatures = new Set<UserExtensionFeature>(['READ','SEND','SHARED_READ','SHARED_SEND','CALENDAR_READ','CALENDAR_WRITE','SHARED_CALENDAR_WRITE']);

/** Navigation carries intent only. Core rechecks account ownership, company
 * policy and product permissions before asking the user for explicit consent. */
export function getUserExtensionsUrl(context: { companyId: string; returnPath: string; features?: readonly UserExtensionFeature[] }): string | null {
  if (!/^[1-9][0-9]{0,18}$/.test(context.companyId) || BigInt(context.companyId) > 9223372036854775807n
    || !/^\/[A-Za-z0-9/_-]*$/.test(context.returnPath) || context.returnPath.includes('//')) return null;
  const core = getCoreApiBaseUrl();
  const applicationCode = String(import.meta.env.VITE_APPLICATION_CODE || '').trim().toUpperCase();
  const requested = [...new Set<UserExtensionFeature>(context.features ?? ['READ'])];
  if (!core || !/^[A-Z][A-Z0-9_-]{1,39}$/.test(applicationCode) || !requested.length || requested.some(feature => !userExtensionFeatures.has(feature))) return null;
  const query = new URLSearchParams({ companyId: context.companyId, applicationCode, features: requested.join(','), returnPath: context.returnPath });
  return `${core}/settings/extensions?${query}`;
}

/** Existing satellite entry points adopt the central reuse/consent flow. */
export function getConnectedEmailAccountsUrl(context: { companyId: string; returnPath: string }): string | null {
  return getUserExtensionsUrl(context);
}

export function getDefaultAdminConsoleUrl(): string {
  const explicit = stripTrailingSlashes(import.meta.env.VITE_PLATFORM_CONSOLE_URL || '');
  if (explicit) return explicit;

  // SYS-CONSOLE, standart SaaS/on-premise kurulumunda platform kamu kökünün
  // `/console` segmentidir. Ayrı host kullanan kurulumlar explicit env ile
  // yukarıdaki yolu ezer; runtime-origin paketleri mevcut origin'e düşer.
  const platform = stripTrailingSlashes(
    import.meta.env.VITE_BASE_URL || import.meta.env.VITE_PLATFORM_URL || ''
  );
  if (platform) return `${platform}/console`;
  if (typeof window !== 'undefined') return `${window.location.origin}/console`;
  return '/console';
}

/** Scoped mailbox administration stays on Core; no mailbox or credential is
 * copied into the satellite's storage or management UI. */
export function getEmailMailboxesUrl(context: { companyId: string }): string | null {
  if (!/^[1-9][0-9]{0,18}$/.test(context.companyId) || BigInt(context.companyId) > 9223372036854775807n) return null;
  const core = getCoreApiBaseUrl();
  return core ? `${core}/settings/email-mailboxes?${new URLSearchParams({ companyId: context.companyId })}` : null;
}
